import axios, { AxiosInstance } from "axios";

/**
 * DTO for individual items in a reimbursement v2 request
 */
export interface ReimbursementV2Item {
  id?: number;
  itemDate: string; // YYYY-MM-DD
  description: string;
  purpose: string;
  quantity: number;
  pricePerUnit: number;
  currency: string;
  forexRate: number;
  taxUnpaid: boolean;
}

/**
 * DTO for reimbursement v2 request
 */
export interface ReimbursementV2Request {
  id?: number;
  requestId?: string;
  employeeId?: number;
  categoryId: number;
  categoryName?: string;
  taxClassificationId?: number;
  taxClassificationName?: string;
  submissionDate: string; // YYYY-MM-DD
  itemDetails: string;
  vendor: string;
  totalAmount: number;
  status?: string;
  attachmentFilePath?: string;
  csvFilePath?: string;
  rejectionComment?: string; // Admin's comment when rejecting
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
  isDeleted?: boolean;
  items: ReimbursementV2Item[];
}

/**
 * API Service for Reimbursement v2 operations
 */
class ReimbursementV2ApiService {
  private api: AxiosInstance;
  private baseUrl: string = "/ofc";

  constructor() {
    this.api = axios.create({
      baseURL: this.baseUrl,
      withCredentials: true,
    });

    // Add request interceptor to handle FormData properly
    this.api.interceptors.request.use((config) => {
      // If the data is FormData, remove Content-Type header to let browser set it
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      }
      return config;
    });
  }

  /**
   * Submit a new reimbursement request
   */
  async submitReimbursementRequest(
    employeeId: number,
    request: ReimbursementV2Request,
    attachmentFile: File,
    username: string,
    mergedFileName?: string,
  ): Promise<ReimbursementV2Request> {
    const formData = new FormData();
    formData.append("employeeId", employeeId.toString());

    // Create a Blob with application/json content type for the request part
    const requestBlob = new Blob([JSON.stringify(request)], {
      type: "application/json",
    });
    formData.append("request", requestBlob);

    formData.append("attachment", attachmentFile);
    formData.append("username", username);

    // Add merged filename if provided
    if (mergedFileName) {
      formData.append("mergedFileName", mergedFileName);
    }

    const response = await this.api.post<ReimbursementV2Request>(
      "/reimbursement/v2/submit",
      formData,
    );
    return response.data;
  }

  /**
   * Get all reimbursement requests for an employee
   */
  async getReimbursementRequestsByEmployee(
    employeeId: number,
  ): Promise<ReimbursementV2Request[]> {
    const response = await this.api.get<ReimbursementV2Request[]>(
      `/reimbursement/v2/list/${employeeId}`,
    );
    return response.data;
  }

  /**
   * Get a specific reimbursement request
   */
  async getReimbursementRequest(
    requestId: string,
  ): Promise<ReimbursementV2Request> {
    const response = await this.api.get<ReimbursementV2Request>(
      `/reimbursement/v2/request/${requestId}`,
    );
    return response.data;
  }

  /**
   * Edit an existing reimbursement request
   * Attachment is now optional - users can keep the previous file or upload a replacement
   */
  async editReimbursementRequest(
    requestId: string,
    request: ReimbursementV2Request,
    attachmentFile: File | null | undefined,
    username: string,
    mergedFileName?: string,
  ): Promise<ReimbursementV2Request> {
    const formData = new FormData();

    // Create a Blob with application/json content type for the request part
    const requestBlob = new Blob([JSON.stringify(request)], {
      type: "application/json",
    });
    formData.append("request", requestBlob);

    // Attachment is now optional
    if (attachmentFile) {
      formData.append("attachment", attachmentFile);
    }

    formData.append("username", username);

    // Add merged filename if provided
    if (mergedFileName) {
      formData.append("mergedFileName", mergedFileName);
    }

    const response = await this.api.put<ReimbursementV2Request>(
      `/reimbursement/v2/edit/${requestId}`,
      formData,
    );
    return response.data;
  }

  /**
   * Check if an attachment exists for a reimbursement request
   * Used to determine if the view attachment button should be enabled
   */
  async checkAttachmentExists(
    requestId: string,
  ): Promise<{ exists: boolean; reason: string }> {
    const response = await this.api.get<{ exists: boolean; reason: string }>(
      `/reimbursement/v2/attachment/exists/${requestId}`,
    );
    return response.data;
  }

  /**
   * Delete a reimbursement request
   */
  async deleteReimbursementRequest(
    requestId: string,
    username: string,
  ): Promise<{ status: string; message: string }> {
    const response = await this.api.delete<{ status: string; message: string }>(
      `/reimbursement/v2/delete/${requestId}`,
      {
        params: {
          username,
        },
      },
    );
    return response.data;
  }

  /**
   * Update request status (admin only)
   */
  async updateReimbursementRequestStatus(
    requestId: string,
    newStatus: string,
    username: string,
    rejectionComment?: string,
  ): Promise<{ status: string; message: string }> {
    const response = await this.api.post<{ status: string; message: string }>(
      `/reimbursement/v2/status/${requestId}`,
      {},
      {
        params: {
          status: newStatus,
          username,
          rejectionComment,
        },
      },
    );
    return response.data;
  }

  /**
   * Get all requests with specific status (admin only)
   */
  async getReimbursementRequestsByStatus(
    status: string,
  ): Promise<ReimbursementV2Request[]> {
    const response = await this.api.get<ReimbursementV2Request[]>(
      `/reimbursement/v2/status/${status}`,
    );
    return response.data;
  }

  /**
   * Get all requests with advanced filtering (admin only)
   */
  async getReimbursementRequestsWithFilters(
    startDate?: string,
    endDate?: string,
    status?: string,
    employeeId?: number,
    searchText?: string,
  ): Promise<ReimbursementV2Request[]> {
    const params: Record<string, string | number> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (status) params.status = status;
    if (employeeId) params.employeeId = employeeId;
    if (searchText) params.searchText = searchText;

    const response = await this.api.get<ReimbursementV2Request[]>(
      `/reimbursement/v2/list/admin`,
      { params },
    );
    return response.data;
  }

  /**
   * Get count of pending reimbursement requests (SUBMITTED or PROCESSING status)
   * Used for badge notification in admin UI
   */
  async getPendingRequestsCount(): Promise<number> {
    const response = await this.api.get<{ count: number }>(
      `/reimbursement/v2/count/pending`,
    );
    return response.data.count;
  }

  /**
   * Get count of reimbursement requests needing attention for an employee (REJECTED or PROCESSED status)
   * Used for badge notification in employee UI
   */
  async getAttentionRequestsCount(employeeId: number): Promise<number> {
    const response = await this.api.get<{ count: number }>(
      `/reimbursement/v2/count/attention/${employeeId}`,
    );
    return response.data.count;
  }

  /**
   * Download attachment PDF for a reimbursement request
   * Returns the PDF as a Blob
   */
  async downloadAttachment(requestId: string): Promise<Blob> {
    const response = await this.api.get(
      `/reimbursement/v2/download/pdf/${requestId}`,
      {
        responseType: "blob",
      },
    );
    return response.data;
  }
}

export default new ReimbursementV2ApiService();
